/**
 * @fileoverview Syndicator Showdown — Authentication Page (Premium Redesign)
 * @module pages/AuthPage
 *
 * Visual reskin to match the LandingPage v3 design language while preserving
 * every behaviour from v2:
 *   - sign in / sign up / reset / Google
 *   - 18+ date-of-birth verification
 *   - email verification holding screen
 *   - friendly Firebase error mapping
 *   - ensureUserDocument(users/{uid}) on first sign-in
 *   - redirect to ?from / ?reason / ?mode=signup query params
 */

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  useNavigate, useLocation, useSearchParams, Link, Navigate,
} from 'react-router-dom';
import {
  signInWithPopup,
  signInWithEmailAndPassword,
  signInWithCustomToken,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  sendEmailVerification,
  updateProfile,
  reload,
  signOut,
  setPersistence,
  browserSessionPersistence,
} from 'firebase/auth';
import { LOGOUT_REASON_STORAGE_KEY } from '../hooks/useInactivityLogout';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { auth, db, googleProvider } from '../services/firebase';
import { PINInput } from '../components/PINInput';
import {
  resolveStoredReferral, recordReferralSignup, clearStoredReferralCode,
} from '../services/referralService';
import {
  getStoredShareAttribution, clearStoredShareAttribution, recordShareSignupAttribution,
} from '../services/shareTrackingService';
import './AuthPage.css';

// ─── Age verification ─────────────────────────────────────────────────────────

function isAgedEighteenOrOver(day, month, year) {
  if (!day || !month || !year) return false;
  const today  = new Date();
  const dob    = new Date(year, month - 1, day);
  const cutoff = new Date(today.getFullYear() - 18, today.getMonth(), today.getDate());
  return dob <= cutoff;
}

// ─── User document creation ───────────────────────────────────────────────────

/**
 * TEMP DIAGNOSTIC + mitigation (P0 auth regression, 2026-07-04).
 *
 * Server-side reproduction (real signInWithPassword + custom-token exchange,
 * both immediately followed by a users/{uid} GET via the raw Firestore REST
 * API) proved the deployed rules already allow this read every time — so a
 * `permission-denied` here in the browser is most consistent with a
 * transient Firestore Web SDK auth-token propagation delay on the very
 * first read issued right after signInWithEmailAndPassword resolves,
 * rather than a persistent rules bug. This retries once, briefly, before
 * giving up — and logs clearly either way so a genuine rules regression is
 * still immediately visible (retry would also fail).
 */
async function getDocWithRetry(ref, { retries = 1, delayMs = 400 } = {}) {
  for (let attempt = 0; ; attempt++) {
    try {
      return await getDoc(ref);
    } catch (err) {
      if (attempt >= retries || err.code !== 'permission-denied') throw err;
      console.warn('[AUTH] Firestore read got permission-denied — retrying once', {
        path: ref.path,
        attempt,
        code: err.code,
      });
      await new Promise((resolve) => { setTimeout(resolve, delayMs); });
    }
  }
}

async function ensureUserDocument(user, extraFields = {}) {
  const ref  = doc(db, 'users', user.uid);
  const snap = await getDocWithRetry(ref);
  if (!snap.exists()) {
    // Invite Friends attribution — if this browser previously visited a
    // /share/invite/:code link, attach the inviter's uid + code to the new
    // account. Best-effort only: never blocks account creation on failure.
    let referral = null;
    try {
      referral = await resolveStoredReferral();
    } catch (err) {
      console.warn('[AuthPage] resolveStoredReferral failed (non-blocking):', err.message);
    }

    // Phase 6 — Challenge a Friend / public share attribution. Separate from
    // (and additive to) the referral code above: a visitor can click
    // "Challenge This Stable" on a public share page without that owner
    // necessarily having a referral code cached locally yet.
    const shareAttribution = getStoredShareAttribution();

    await setDoc(ref, {
      id:                user.uid,
      email:             user.email       ?? null,
      displayName:       user.displayName ?? user.email?.split('@')[0] ?? 'Player',
      photoURL:          user.photoURL    ?? null,
      role:              'player',
      createdAt:         serverTimestamp(),
      foundingMember:    true,
      qualifiedForFinal: {},
      ageVerified:       extraFields.ageVerified ?? false,
      dateOfBirth:       extraFields.dateOfBirth ?? null,
      // Attribution for the *inviter* — distinct from this user's own
      // `referralCode` (generated later, see referralService.getOrCreateReferralCode),
      // which is the code THEY give out to invite others.
      referredByUid:     referral?.referredByUid ?? shareAttribution?.ownerUid ?? null,
      referredByCode:    referral?.referralCode  ?? null,
      sourceShareId:     shareAttribution?.shareId ?? null,
      ...extraFields,
    });

    if (referral?.referralCode) {
      recordReferralSignup(referral.referralCode);
      clearStoredReferralCode();
    }
    if (shareAttribution?.shareId) {
      recordShareSignupAttribution(shareAttribution.shareId);
      clearStoredShareAttribution();
    }
  } else {
    // Existing account — safe login-heartbeat touch-up only. merge:true and
    // an explicit minimal field set means this can never overwrite/clear
    // role, pinHash, or any other field it doesn't name, and the rules
    // denylist (see firestore.rules) blocks it from ever touching a
    // protected field even if this list is extended carelessly later.
    try {
      const touchUp = {
        lastLoginAt: serverTimestamp(),
        updatedAt:   serverTimestamp(),
      };
      // Public Stable Profile QA fix (2026-07-06): a Google sign-in's photo
      // only ever lived on the ephemeral Auth session, never persisted to
      // Firestore on repeat logins — so it was invisible to any surface
      // that isn't the currently-signed-in browser (e.g. the public stable
      // profile, which can only read this user's own Firestore docs).
      // Backfill it here, once it appears, without ever clearing an
      // existing value if Auth momentarily has none this session.
      const existingPhotoURL = snap.data()?.photoURL || null;
      if (user.photoURL && user.photoURL !== existingPhotoURL) {
        touchUp.photoURL = user.photoURL;
      }
      await setDoc(ref, touchUp, { merge: true });

      if (touchUp.photoURL) {
        // player_profiles/{uid} is the public-safe mirror the profile page
        // actually reads — photoURL is on its client-writable allow-list
        // (see firestore.rules). Best-effort merge; never blocks login.
        await setDoc(doc(db, 'player_profiles', user.uid), {
          photoURL:  touchUp.photoURL,
          updatedAt: serverTimestamp(),
        }, { merge: true }).catch(() => {});
      }
    } catch (err) {
      console.warn('[AuthPage] ensureUserDocument login touch-up failed (non-blocking):', err.code, err.message);
    }
  }
}

// ─── Friendly error messages ─────────────────────────────────────────────────
//
// TEMP DIAGNOSTIC (P0 auth regression, see AuthPage handleSubmit): every
// signInWithEmailAndPassword failure is logged in full (code/message/name,
// never the password) via console.error so the real Firebase Auth error is
// visible in prod DevTools instead of only this generic mapped string.

function getFriendlyAuthError(code) {
  const map = {
    'auth/wrong-password':            'Incorrect password.',
    'auth/invalid-credential':        'Incorrect email or password.',
    'auth/invalid-login-credentials': 'Incorrect email or password.',
    'auth/user-not-found':            'No account for that email address.',
    'auth/user-disabled':             'This account has been disabled. Contact admin@syndicatorshowdown.com.au.',
    'auth/email-already-in-use':      'An account already exists for this email. Sign in instead.',
    'auth/weak-password':             'Password must be at least 6 characters.',
    'auth/invalid-email':             'Please enter a valid email address.',
    'auth/too-many-requests':         'Too many attempts. Please wait a moment and try again.',
    'auth/popup-closed-by-user':      'Sign-in window closed. Please try again.',
    'auth/network-request-failed':    'Network error. Check your connection and try again.',
    'auth/operation-not-allowed':     'Email/password sign-in is temporarily unavailable. Please try again shortly.',
    'auth/api-key-not-valid':         'Configuration error. Please refresh the page and try again.',
    'auth/configuration-not-found':   'Configuration error. Please refresh the page and try again.',
  };
  return map[code] || 'Something went wrong. Please try again.';
}

// Back-compat alias — used by handleGoogle/handlePINSubmit and any other
// call sites that predate the rename above.
const friendlyError = getFriendlyAuthError;

// ─── Inline icons ─────────────────────────────────────────────────────────────

const IconUser = (p) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" {...p}>
    <circle cx="12" cy="8" r="4" stroke="currentColor" strokeWidth="1.7"/>
    <path d="M4 21c0-4 4-6 8-6s8 2 8 6" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"/>
  </svg>
);
const IconMail = (p) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" {...p}>
    <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="1.7"/>
    <path d="M3 7l9 6 9-6" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const IconLock = (p) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" {...p}>
    <rect x="4" y="11" width="16" height="10" rx="2" stroke="currentColor" strokeWidth="1.7"/>
    <path d="M8 11V8a4 4 0 0 1 8 0v3" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"/>
  </svg>
);
const IconEye = ({ open = true, ...p }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" {...p}>
    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z"
          stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
    <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.7"/>
    {!open && (
      <path d="M3 3l18 18" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"/>
    )}
  </svg>
);
const IconCalendar = (p) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" {...p}>
    <rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.7"/>
    <path d="M3 9h18M8 3v4M16 3v4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"/>
  </svg>
);
const IconChevron = (p) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" {...p}>
    <path d="M15 6l-6 6 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const IconCheck = (p) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" {...p}>
    <path d="M5 12l5 5 9-11" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const IconShield = (p) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" {...p}>
    <path d="M12 2 4 5v6c0 5 3.5 9 8 11 4.5-2 8-6 8-11V5l-8-3Z"
          stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round"/>
    <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const IconGoogle = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

// ─── Password strength helper ─────────────────────────────────────────────────

function scorePassword(pw) {
  if (!pw) return { score: 0, label: '', tone: '' };
  let s = 0;
  if (pw.length >= 8)        s++;
  if (/[a-z]/.test(pw))      s++;
  if (/[A-Z]/.test(pw))      s++;
  if (/[0-9]/.test(pw))      s++;
  if (/[^A-Za-z0-9]/.test(pw)) s++;
  const labels = ['', 'Very weak', 'Weak', 'Fair', 'Good', 'Strong password'];
  const tones  = ['', 'red',       'red',  'amber','amber','green'];
  return { score: s, label: labels[s], tone: tones[s] };
}

const emailValid = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e.trim());

// ─── Email-verification holding screen ───────────────────────────────────────

const VerifyEmailScreen = ({ email, onResend, onSignOut }) => {
  const [resent, setResent] = useState(false);
  const handleResend = async () => { await onResend(); setResent(true); };

  return (
    <div className="ss-auth-page">
      <header className="ss-auth-header">
        <Link to="/" className="auth-header-brand">
          <span className="auth-brand-title">SYNDICATOR SHOWDOWN</span>
          <span className="auth-brand-slogan">OWN THE DREAM. COMPETE FOR GLORY.</span>
        </Link>
        <Link to="/" className="ss-auth-back">
          <IconChevron/> Back to Home
        </Link>
      </header>

      <main className="ss-auth-main ss-auth-main--centered">
        <div className="ss-auth-card">
          <div className="ss-auth-card-icon">✉</div>
          <h2 className="ss-auth-card-title">CHECK YOUR EMAIL</h2>
          <p className="ss-auth-card-sub">
            We sent a verification link to <strong>{email}</strong>.<br/>
            Click the link in that email to activate your account.
          </p>
          <div className="ss-auth-card-rule"/>

          <p className="ss-auth-help-text">
            Once verified, return here and sign in. Check your spam folder if
            the email hasn't arrived in a few minutes.
          </p>

          <div style={{
            backgroundColor: 'rgba(234, 179, 8, 0.1)',
            border: '1px solid #EAB308',
            borderRadius: '8px',
            padding: '12px 16px',
            marginTop: '16px',
            display: 'flex',
            gap: '10px',
            alignItems: 'flex-start',
          }}>
            <span style={{ fontSize: '18px', flexShrink: 0 }}>📬</span>
            <p style={{
              margin: 0,
              color: '#FDE68A',
              fontSize: '13px',
              lineHeight: '1.5',
            }}>
              <strong>Can't find the email?</strong> Check your{' '}
              <strong>Junk</strong> or <strong>Spam</strong> folder
              — verification emails sometimes get filtered automatically.
              If it's there, mark it as "Not Spam" to receive future
              emails from us directly.
            </p>
          </div>

          {resent ? (
            <p className="ss-auth-success-msg">
              <IconCheck/> Verification email resent
            </p>
          ) : (
            <button
              className="ss-auth-submit-btn ss-auth-submit-btn--ghost"
              onClick={handleResend}
              type="button"
            >
              RESEND VERIFICATION EMAIL
            </button>
          )}

          <button
            className="ss-auth-text-link"
            onClick={onSignOut}
            type="button"
          >
            ← Back to sign in
          </button>
        </div>
      </main>
    </div>
  );
};

// ─── Main AuthPage ────────────────────────────────────────────────────────────

export const AuthPage = ({ verifyMode = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const from = location.state?.from?.pathname || '/';

  // The inactivity guard can sign a user out from *any* tab/route, so it
  // can't rely on router state (that only exists on the tab that actually
  // called navigate()). It stores a short, non-sensitive reason in
  // sessionStorage instead. Read is a pure/idempotent lazy-init (safe if
  // React's StrictMode dev double-invoke calls it twice); the clear is a
  // separate effect so it doesn't resurface on a later unrelated visit.
  const [storedLogoutReason] = useState(() => {
    try {
      return sessionStorage.getItem(LOGOUT_REASON_STORAGE_KEY);
    } catch {
      return null;
    }
  });
  useEffect(() => {
    try { sessionStorage.removeItem(LOGOUT_REASON_STORAGE_KEY); } catch { /* ignore */ }
  }, []);
  const reason = storedLogoutReason || location.state?.reason;

  // Initial mode comes from ?mode=signup (or signin), defaults to signin
  const initialMode = searchParams.get('mode') === 'signup' ? 'signup' : 'signin';

  const [mode,           setMode]           = useState(initialMode);
  const [email,          setEmail]          = useState('');
  const [password,       setPassword]       = useState('');
  const [showPassword,   setShowPassword]   = useState(false);
  const [displayName,    setDisplayName]    = useState('');
  const [dobDay,         setDobDay]         = useState('');
  const [dobMonth,       setDobMonth]       = useState('');
  const [dobYear,        setDobYear]        = useState('');
  const [loading,        setLoading]        = useState(false);
  const [googleLoading,  setGoogleLoading]  = useState(false);
  const [error,          setError]          = useState(null);
  const [resetSent,      setResetSent]      = useState(false);
  const [awaitingVerify, setAwaitingVerify] = useState(false);
  const [currentUser,    setCurrentUser]    = useState(null);
  const [alreadyAuthed,  setAlreadyAuthed]  = useState(false);
  // ── PIN login state
  const [pinAvailable,   setPinAvailable]   = useState(false);
  const [checkingPin,    setCheckingPin]    = useState(false);
  const [loginMethod,    setLoginMethod]    = useState('password'); // 'password' | 'pin'
  const [pinValue,       setPinValue]       = useState('');
  const [pinLoading,     setPinLoading]     = useState(false);
  const [pinError,       setPinError]       = useState(null);
  const [signupPin,         setSignupPin]         = useState('');
  const [signupPinConfirm,  setSignupPinConfirm]  = useState('');
  const [signupPinError,    setSignupPinError]    = useState('');
  const pinCheckedEmail  = useRef('');

  // Check auth state ONCE on mount — do not use an ongoing onAuthStateChanged
  // subscription here. A live subscription fires when handleSubmit /
  // handlePINSubmit / handleGoogle log the user in, which sets alreadyAuthed=true
  // and triggers <Navigate to={from}> at the same time as the explicit navigate()
  // call in those handlers. Two navigations in the same React batch cause rapid
  // mount→unmount→remount of downstream components and trigger React error #300.
  //
  // AuthGate (in App.jsx) guarantees that onAuthStateChanged has already resolved
  // before any route renders, so auth.currentUser is synchronously available here.
  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;
    const isGoogleUser = user.providerData.some(p => p.providerId === 'google.com');
    if (user.emailVerified || isGoogleUser) {
      setAlreadyAuthed(true);
    } else {
      setCurrentUser(user);
      setAwaitingVerify(true);
    }
  }, []);

  // ── Derived flags — MUST be above all early returns (Rules of Hooks)
  const pwScore = useMemo(() => scorePassword(password), [password]);

  // ── Early exits (after every hook call)
  if (alreadyAuthed) return <Navigate to={from} replace />;

  if (awaitingVerify && currentUser) {
    return (
      <VerifyEmailScreen
        email={currentUser.email}
        onResend={async () => {
          await reload(currentUser);
          await sendEmailVerification(currentUser);
        }}
        onSignOut={async () => {
          localStorage.removeItem('ss_login_time');
          await auth.signOut();
          setAwaitingVerify(false);
          setCurrentUser(null);
        }}
      />
    );
  }
  const nameValid     = displayName.trim().length >= 2;
  const isEmailValid  = emailValid(email);
  const pwBaseValid   = password.length >= 6;

  // ── Handlers
  const handleDobChange = (field, value) => {
    if (field === 'day')   setDobDay(value);
    if (field === 'month') setDobMonth(value);
    if (field === 'year')  setDobYear(value);
  };

  const handleGoogle = async () => {
    setGoogleLoading(true);
    setError(null);
    try {
      // Session-only persistence: this account must not survive a full
      // browser/computer restart. Set defensively right before sign-in so
      // it's guaranteed to apply even if the module-level call in
      // services/firebase.js hasn't resolved yet.
      await setPersistence(auth, browserSessionPersistence);
      const result = await signInWithPopup(auth, googleProvider);

      // Check exclusion for Google sign-in
      try {
        const checkExclusion = httpsCallable(
          getFunctions(undefined, 'australia-southeast1'), 'checkEmailExclusion'
        );
        const exclusionResult = await checkExclusion({ email: result.user.email });
        if (exclusionResult.data?.excluded) {
          // Sign them back out immediately before they get access
          await signOut(auth);
          if (exclusionResult.data?.permanent) {
            throw new Error(
              'This account has been permanently closed and is no longer eligible ' +
              'to access Syndicator Showdown. If you need support, call Gambling ' +
              'Help Online on 1800 858 858 (free, 24/7).'
            );
          } else {
            throw new Error(
              'This account has been deactivated. Please contact ' +
              'admin@syndicatorshowdown.com.au to discuss reinstatement.'
            );
          }
        }
      } catch (err) {
        if (err.message?.includes('permanently closed') ||
            err.message?.includes('deactivated')) {
          throw err;
        }
        console.warn('[AuthPage] Google sign-in exclusion check failed (non-blocking):', err.message);
      }

      await ensureUserDocument(result.user, { ageVerified: true, dateOfBirth: null });
      localStorage.setItem('ss_login_time', Date.now().toString());
      const destination = (from && from !== '/') ? from : '/dashboard';
      navigate(destination, { replace: true });
    } catch (err) {
      setError(friendlyError(err.code));
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    if (mode === 'signup') {
      if (!dobDay || !dobMonth || !dobYear) {
        setError('Please enter your date of birth.');
        return;
      }
      if (!isAgedEighteenOrOver(dobDay, dobMonth, dobYear)) {
        setError('You must be 18 or older to play Syndicator Showdown. Please visit a responsible gambling service if you need support.');
        return;
      }
    }

    setLoading(true);
    setError(null);
    try {
      // Session-only persistence — see handleGoogle for why this is set
      // defensively right before the auth call rather than relying solely
      // on the module-level setPersistence() in services/firebase.js.
      await setPersistence(auth, browserSessionPersistence);

      if (mode === 'reset') {
        await sendPasswordResetEmail(auth, email);
        setResetSent(true);
        return;
      }

      if (mode === 'signup') {
        // Check email exclusion registry before creating account
        try {
          const checkExclusion = httpsCallable(
            getFunctions(undefined, 'australia-southeast1'), 'checkEmailExclusion'
          );
          const exclusionResult = await checkExclusion({ email });
          if (exclusionResult.data?.excluded) {
            if (exclusionResult.data?.permanent) {
              throw new Error(
                'This email address is not eligible to register for Syndicator Showdown. ' +
                'If you believe this is an error, contact admin@syndicatorshowdown.com.au. ' +
                'If you need support, call Gambling Help Online on 1800 858 858.'
              );
            } else {
              throw new Error(
                'This account has been deactivated. Please contact ' +
                'admin@syndicatorshowdown.com.au to discuss reinstatement.'
              );
            }
          }
        } catch (err) {
          // Only block if it's our exclusion error — don't block on network failures
          if (err.message?.includes('not eligible') || err.message?.includes('deactivated')) {
            throw err;
          }
          // Network/function error — log but allow registration to proceed
          console.warn('[AuthPage] Exclusion check failed (non-blocking):', err.message);
        }

        const result = await createUserWithEmailAndPassword(auth, email, password);
        if (displayName.trim()) {
          await updateProfile(result.user, { displayName: displayName.trim() });
        }
        await ensureUserDocument(result.user, {
          ageVerified: true,
          dateOfBirth: `${dobYear}-${String(dobMonth).padStart(2,'0')}-${String(dobDay).padStart(2,'0')}`,
          displayName: displayName.trim() || result.user.email.split('@')[0],
        });
        await sendEmailVerification(result.user);

        // If player set a PIN during signup, save it now
        const cleanPin = signupPin.replace(/\u200b/g, '');
        if (cleanPin.length === 6 && cleanPin === signupPinConfirm) {
          try {
            const setPin = httpsCallable(getFunctions(undefined, 'australia-southeast1'), 'setUserPin');
            await setPin({ pin: cleanPin });
          } catch (pinErr) {
            // Non-fatal — PIN can be set later from dashboard
            console.warn('[AuthPage] PIN setup during signup failed:', pinErr.message);
          }
        }

        setCurrentUser(result.user);
        setAwaitingVerify(true);
      } else {
        // Check exclusion registry before allowing sign-in
        // Catches the edge case where a banned user's Firebase Auth was manually re-enabled
        try {
          const checkExclusion = httpsCallable(
            getFunctions(undefined, 'australia-southeast1'), 'checkEmailExclusion'
          );
          const exclusionResult = await checkExclusion({ email });
          if (exclusionResult.data?.excluded) {
            if (exclusionResult.data?.permanent) {
              throw new Error(
                'This account has been permanently closed and is no longer eligible ' +
                'to access Syndicator Showdown. If you need support, call Gambling ' +
                'Help Online on 1800 858 858 (free, 24/7).'
              );
            } else {
              throw new Error(
                'This account has been deactivated. Please contact ' +
                'admin@syndicatorshowdown.com.au to discuss reinstatement.'
              );
            }
          }
        } catch (err) {
          if (err.message?.includes('permanently closed') ||
              err.message?.includes('deactivated')) {
            throw err;
          }
          // Network or function error — non-blocking, let sign-in proceed
          console.warn('[AuthPage] Sign-in exclusion check failed (non-blocking):', err.message);
        }

        // ── Step 1: the REAL Firebase Auth call, isolated in its own
        // try/catch. Only a failure HERE means the email/password was
        // rejected — anything that goes wrong after this point (Firestore
        // profile reads/writes, role lookup) must never be reported to the
        // user as a password/login failure (see Step 2 below).
        let credential;
        try {
          console.info('[AUTH] Attempting email/password sign-in', {
            email: email.trim().toLowerCase(),
          });

          credential = await signInWithEmailAndPassword(auth, email.trim(), password);

          console.info('[AUTH] signInWithEmailAndPassword succeeded', {
            uid: credential.user.uid,
            email: credential.user.email,
          });
        } catch (authError) {
          console.error('[AUTH] signInWithEmailAndPassword failed', {
            code: authError.code,
            message: authError.message,
            name: authError.name,
          });
          console.info('[AUTH] currentUser after failure', {
            uid: auth.currentUser?.uid ?? null,
            email: auth.currentUser?.email ?? null,
          });
          setError(getFriendlyAuthError(authError.code));
          setLoading(false);
          return;
        }

        if (!credential.user.emailVerified) {
          setCurrentUser(credential.user);
          setAwaitingVerify(true);
          setLoading(false);
          return;
        }

        // ── Step 2: post-login app bootstrap (profile doc + role lookup).
        // Auth has ALREADY succeeded at this point (uncondtionally proven by
        // reaching this line). `navigate()` below is called exactly once, no
        // matter what happens in this block — a Firestore/profile hiccup
        // must route the user into the app, never back to "your password is
        // wrong", and never leave them stranded on /signin.
        try {
          await ensureUserDocument(credential.user);
          console.info('[AUTH] Profile bootstrap succeeded', { uid: credential.user.uid });
        } catch (bootstrapError) {
          console.warn('[AUTH] Profile bootstrap failed but login will continue', {
            uid: credential.user.uid,
            code: bootstrapError.code,
            message: bootstrapError.message,
          });
        }

        localStorage.setItem('ss_login_time', Date.now().toString());

        // Best-effort role lookup for ROUTING ONLY (admin vs dashboard) —
        // never blocks navigation. Defaults to the player destination if it
        // fails for any reason; the admin route itself (ProtectedRoute) does
        // its own independent, equally non-blocking role check.
        let destination = (from && from !== '/') ? from : '/dashboard';
        try {
          console.info('[AUTH] Before user profile read', {
            authCurrentUserUid: auth.currentUser?.uid ?? null,
            targetUid: credential.user.uid,
            sameUid: auth.currentUser?.uid === credential.user.uid,
          });
          // Force a fresh ID token — rules out any stale-token propagation
          // delay between signInWithEmailAndPassword resolving and this read.
          await credential.user.getIdToken(true);
          console.info('[AUTH] Fresh ID token acquired before profile read', {
            uid: credential.user.uid,
          });

          const userDoc = await getDocWithRetry(doc(db, 'users', credential.user.uid));
          if (userDoc.exists() && userDoc.data().role === 'admin') {
            destination = '/admin';
          }
        } catch (roleLookupError) {
          console.warn('[AUTH] Role lookup failed — routing to default destination', {
            uid: credential.user.uid,
            code: roleLookupError.code,
            message: roleLookupError.message,
          });
        }

        navigate(destination, { replace: true });
      }
    } catch (err) {
      // Reached only by the reset/signup branches above, or by the
      // exclusion-check re-throws (banned/deactivated) — those are plain
      // Errors with a real user-facing message and no Firebase `.code`, so
      // show that message rather than collapsing it to the generic fallback.
      setError(!err.code && err.message ? err.message : getFriendlyAuthError(err.code));
    } finally {
      setLoading(false);
    }
  };

  const switchMode = (next) => {
    setMode(next);
    setError(null);
    setResetSent(false);
    setPinAvailable(false);
    setLoginMethod('password');
    setPinValue('');
    setPinError(null);
    pinCheckedEmail.current = '';
  };

  // Check whether the entered email has PIN enabled (fires on email blur in signin)
  const handleEmailBlur = useCallback(async () => {
    if (mode !== 'signin' || !emailValid(email) || email === pinCheckedEmail.current) return;
    pinCheckedEmail.current = email;
    setCheckingPin(true);
    console.log('[PIN] Checking PIN status for:', email);
    try {
      const fn = httpsCallable(getFunctions(undefined, 'australia-southeast1'), 'checkPinStatus');
      const { data } = await fn({ email });
      console.log('[PIN] checkPinStatus result:', data);
      setPinAvailable(!!data.pinEnabled);
    } catch (err) {
      console.error('[PIN] checkPinStatus error:', err);
      setPinAvailable(false);
    } finally {
      setCheckingPin(false);
    }
  }, [mode, email]);

  // PIN login: verify PIN via CF → signInWithCustomToken → navigate
  const handlePINSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (pinValue.length !== 6 || pinLoading) return;
    setPinLoading(true);
    setPinError(null);
    try {
      // Session-only persistence — see handleGoogle for why this is set
      // defensively right before the auth call.
      await setPersistence(auth, browserSessionPersistence);

      // Exclusion check on PIN login path
      try {
        const checkExclusion = httpsCallable(
          getFunctions(undefined, 'australia-southeast1'), 'checkEmailExclusion'
        );
        const exclusionResult = await checkExclusion({ email });
        if (exclusionResult.data?.excluded) {
          if (exclusionResult.data?.permanent) {
            throw new Error(
              'This account has been permanently closed and is no longer eligible ' +
              'to access Syndicator Showdown. If you need support, call Gambling ' +
              'Help Online on 1800 858 858 (free, 24/7).'
            );
          } else {
            throw new Error(
              'This account has been deactivated. Please contact ' +
              'admin@syndicatorshowdown.com.au to discuss reinstatement.'
            );
          }
        }
      } catch (err) {
        if (err.message?.includes('permanently closed') ||
            err.message?.includes('deactivated')) {
          throw err;
        }
        console.warn('[AuthPage] PIN sign-in exclusion check failed (non-blocking):', err.message);
      }

      const verify = httpsCallable(getFunctions(undefined, 'australia-southeast1'), 'verifyPinAndGetToken');
      const { data } = await verify({ email, pin: pinValue });
      const cred = await signInWithCustomToken(auth, data.token);
      localStorage.setItem('ss_login_time', Date.now().toString());
      const userDoc  = await getDoc(doc(db, 'users', cred.user.uid));
      const userData = userDoc.exists() ? userDoc.data() : {};

      // Admin-initiated PIN reset — force new PIN before entering app
      if (userData.pinResetRequired === true) {
        const expiry = userData.pinResetExpiry?.toDate?.();
        if (expiry && expiry < new Date()) {
          await signOut(auth);
          setPinError(
            'Your temporary PIN has expired. Please use your email and password to log in, or contact support.'
          );
          return;
        }
        navigate('/pin-setup?reset=true', { replace: true });
        return;
      }

      if (userData.role === 'admin') {
        navigate('/admin', { replace: true });
      } else {
        navigate((from && from !== '/') ? from : '/dashboard', { replace: true });
      }
    } catch (err) {
      setPinError(err.message || 'Something went wrong. Try password login.');
      setPinValue(''); // Clear digits so user can re-enter cleanly
    } finally {
      setPinLoading(false);
    }
  }, [email, pinValue, pinLoading, from, navigate]);

  const TITLES = {
    signin: 'WELCOME BACK',
    signup: 'CREATE YOUR ACCOUNT',
    reset:  'RESET PASSWORD',
  };
  const SUBTITLES = {
    signin: 'Sign in to continue building your champion stable.',
    signup: 'Join thousands of racing fans and start competing.',
    reset:  "Enter your email and we'll send you a reset link.",
  };
  const CTA_LABELS = {
    signin: loginMethod === 'pin' ? 'LOGIN WITH PIN' : 'SIGN IN',
    signup: 'CREATE ACCOUNT',
    reset:  'SEND RESET LINK',
  };

  // ── DoB select options
  const days   = Array.from({ length: 31 }, (_, i) => i + 1);
  const months = ['January','February','March','April','May','June',
                  'July','August','September','October','November','December'];
  const currentYear = new Date().getFullYear();
  const years  = Array.from({ length: 100 }, (_, i) => currentYear - i);

  return (
    <div className="ss-auth-page">

      {/* ── Top header strip ────────────────────────────────────────────── */}
      <header className="ss-auth-header">
        <Link to="/" className="auth-header-brand">
          <span className="auth-brand-title">SYNDICATOR SHOWDOWN</span>
          <span className="auth-brand-slogan">OWN THE DREAM. COMPETE FOR GLORY.</span>
        </Link>
        <Link to="/" className="ss-auth-back">
          <IconChevron/> Back to Home
        </Link>
      </header>

      {/* ── Main card ───────────────────────────────────────────────────── */}
      <main className="ss-auth-main">

        {reason === 'auth_required' && (
          <div className="ss-auth-banner">
            Sign in to enter tournaments and track your stable.
          </div>
        )}

        {reason === 'session_expired' && (
          <div className="ss-auth-banner ss-auth-banner--warning">
            Your session has expired for security. Please log in again.
          </div>
        )}

        {reason === 'inactive' && (
          <div className="ss-auth-banner ss-auth-banner--warning">
            You were signed out for your safety after a period of inactivity.
          </div>
        )}

        <div className="ss-auth-card">
          <div className="ss-auth-card-icon">
            <IconUser/>
          </div>
          <h2 className="ss-auth-card-title">{TITLES[mode]}</h2>
          <p className="ss-auth-card-sub">{SUBTITLES[mode]}</p>
          <div className="ss-auth-card-rule"/>

          {/* Google button — not shown on reset */}
          {mode !== 'reset' && (
            <>
              <button
                className="ss-auth-google-btn"
                onClick={handleGoogle}
                disabled={googleLoading}
                type="button"
              >
                <IconGoogle/>
                {googleLoading ? 'Signing in…' : 'Continue with Google'}
              </button>
              <div className="ss-auth-or-divider"><span>OR</span></div>
            </>
          )}

          {resetSent ? (
            <div className="ss-auth-success-card">
              <div className="ss-auth-success-icon"><IconCheck/></div>
              <p>Reset link sent to <strong>{email}</strong>.</p>
              <p className="ss-auth-help-text">
                Check your inbox and follow the link to set a new password.
              </p>
              <button
                className="ss-auth-text-link"
                onClick={() => switchMode('signin')}
                type="button"
              >
                ← Back to sign in
              </button>
            </div>
          ) : (
            <form className="ss-auth-form" onSubmit={mode === 'signin' && loginMethod === 'pin' ? handlePINSubmit : handleSubmit} noValidate>

              {/* DISPLAY NAME — signup only */}
              {mode === 'signup' && (
                <div className="ss-auth-field">
                  <label htmlFor="ss-name" className="ss-auth-label">DISPLAY NAME</label>
                  <div className={`ss-auth-input-wrap ${displayName && nameValid ? 'is-valid' : ''}`}>
                    <span className="ss-auth-input-icon"><IconUser/></span>
                    <input
                      id="ss-name"
                      type="text"
                      className="ss-auth-input"
                      value={displayName}
                      onChange={e => setDisplayName(e.target.value)}
                      placeholder="Your leaderboard name"
                      autoComplete="name"
                      required
                    />
                    {displayName && nameValid && (
                      <span className="ss-auth-input-valid"><IconCheck/></span>
                    )}
                  </div>
                </div>
              )}

              {/* EMAIL */}
              <div className="ss-auth-field">
                <label htmlFor="ss-email" className="ss-auth-label">EMAIL ADDRESS</label>
                <div className={`ss-auth-input-wrap ${email && isEmailValid ? 'is-valid' : ''}`}>
                  <span className="ss-auth-input-icon"><IconMail/></span>
                  <input
                    id="ss-email"
                    type="email"
                    className="ss-auth-input"
                    value={email}
                    onChange={e => {
                      const val = e.target.value;
                      setEmail(val);
                      if (val !== pinCheckedEmail.current) {
                        setPinAvailable(false);
                        setLoginMethod('password');
                        setPinValue('');
                        setPinError(null);
                      }
                      // Auto-trigger PIN check 600ms after user stops typing a valid email
                      if (emailValid(val) && val !== pinCheckedEmail.current) {
                        clearTimeout(window._pinCheckTimer);
                        window._pinCheckTimer = setTimeout(() => {
                          pinCheckedEmail.current = '';
                          handleEmailBlur();
                        }, 600);
                      }
                    }}
                    onBlur={handleEmailBlur}
                    placeholder="you@example.com"
                    autoComplete="email"
                    required
                  />
                  {email && isEmailValid && (
                    <span className="ss-auth-input-valid"><IconCheck/></span>
                  )}
                </div>
              </div>

              {/* Method toggle — always visible in signin mode */}
              {mode === 'signin' && (
                <div>
                  {checkingPin && (
                    <p style={{
                      fontSize: 11, color: 'rgba(255,255,255,0.35)',
                      margin: '-4px 0 8px', textAlign: 'center',
                    }}>
                      Checking login options…
                    </p>
                  )}
                  <div style={{ display: 'flex', gap: 6, marginBottom: 2 }}>
                    <button
                      type="button"
                      onClick={() => { setLoginMethod('password'); setPinValue(''); setPinError(null); }}
                      style={{
                        flex: 1, padding: '9px 0',
                        background: loginMethod === 'password' ? '#1A4FFF' : 'rgba(255,255,255,0.05)',
                        border: loginMethod === 'password' ? 'none' : '1px solid rgba(255,255,255,0.14)',
                        borderRadius: 8, color: '#fff', cursor: 'pointer',
                        fontSize: 12, fontWeight: 700, letterSpacing: '0.06em',
                      }}
                    >
                      🔑 Password
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setLoginMethod('pin');
                        setError(null);
                        setPinError(null);
                      }}
                      style={{
                        flex: 1, padding: '9px 0',
                        background: loginMethod === 'pin' ? '#1A4FFF' : 'rgba(255,255,255,0.05)',
                        border: loginMethod === 'pin' ? 'none' : '1px solid rgba(255,255,255,0.14)',
                        borderRadius: 8, color: pinAvailable ? '#fff' : 'rgba(255,255,255,0.35)',
                        cursor: 'pointer',
                        fontSize: 12, fontWeight: 700, letterSpacing: '0.06em',
                      }}
                    >
                      🔢 PIN {!isEmailValid ? '' : pinAvailable ? '' : '(not set)'}
                    </button>
                  </div>
                  {isEmailValid && !checkingPin && email === pinCheckedEmail.current && !pinAvailable && loginMethod === 'password' && (
                    <p style={{
                      fontSize: 11, color: 'rgba(255,255,255,0.25)',
                      margin: '4px 0 0', textAlign: 'center',
                    }}>
                      No PIN set for this account
                    </p>
                  )}
                </div>
              )}

              {/* PASSWORD — not shown on reset or when PIN mode active */}
              {mode !== 'reset' && loginMethod !== 'pin' && (
                <div className="ss-auth-field">
                  <div className="ss-auth-label-row">
                    <label htmlFor="ss-pw" className="ss-auth-label">PASSWORD</label>
                    {mode === 'signin' && (
                      <button
                        type="button"
                        className="ss-auth-text-link ss-auth-text-link--inline"
                        onClick={() => switchMode('reset')}
                      >
                        Forgot password?
                      </button>
                    )}
                  </div>
                  <div className={`ss-auth-input-wrap ${password && pwBaseValid ? 'is-valid' : ''}`}>
                    <span className="ss-auth-input-icon"><IconLock/></span>
                    <input
                      id="ss-pw"
                      type={showPassword ? 'text' : 'password'}
                      className="ss-auth-input"
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      placeholder={mode === 'signup' ? 'At least 8 characters' : ''}
                      autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
                      required={loginMethod === 'password'}
                    />
                    <button
                      type="button"
                      className="ss-auth-input-eye"
                      onClick={() => setShowPassword(s => !s)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      <IconEye open={!showPassword}/>
                    </button>
                  </div>

                  {/* Password strength — signup only */}
                  {mode === 'signup' && password && (
                    <div className="ss-auth-pw-strength">
                      <div className="ss-auth-pw-bars">
                        {[1,2,3,4,5].map(i => (
                          <span
                            key={i}
                            className={`ss-auth-pw-bar ${i <= pwScore.score ? `is-${pwScore.tone}` : ''}`}
                          />
                        ))}
                      </div>
                      {pwScore.label && (
                        <span className={`ss-auth-pw-label is-${pwScore.tone}`}>
                          {pwScore.label}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* PIN INPUT — signin + pin mode only */}
              {mode === 'signin' && loginMethod === 'pin' && (
                <div className="ss-auth-field">
                  <label className="ss-auth-label">ENTER YOUR 6-DIGIT PIN</label>
                  <PINInput
                    value={pinValue}
                    onChange={setPinValue}
                    disabled={pinLoading}
                  />
                  {pinError && (
                    <p style={{
                      background: 'rgba(255,75,75,0.10)', border: '1px solid rgba(255,75,75,0.4)',
                      color: '#FFB4B4', borderRadius: 10, padding: '10px 14px',
                      fontSize: 13, textAlign: 'center', margin: '4px 0 0',
                    }}>
                      {pinError}
                    </p>
                  )}
                  <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', textAlign: 'center', margin: '6px 0 0' }}>
                    Forgot PIN? Log in with your password to reset it.
                  </p>
                </div>
              )}

              {/* PIN SETUP — signup only */}
              {mode === 'signup' && (
                <div className="ss-auth-field">
                  <label className="ss-auth-label">
                    CREATE A LOGIN PIN{' '}
                    <span style={{ color: 'rgba(255,255,255,0.35)', fontSize: 10, fontWeight: 400, letterSpacing: 0 }}>(optional)</span>
                  </label>
                  <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', margin: '0 0 10px', lineHeight: 1.5 }}>
                    Set a 6-digit PIN now for faster sign-in next time. You can also do this later from your dashboard.
                  </p>

                  {!signupPin && signupPin.length === 0 && (
                    <button
                      type="button"
                      onClick={() => setSignupPin('\u200b')}
                      style={{
                        background: 'rgba(255,255,255,0.05)',
                        border: '1px dashed rgba(255,255,255,0.2)',
                        borderRadius: 8, color: 'rgba(255,255,255,0.5)',
                        fontSize: 13, padding: '9px 16px',
                        cursor: 'pointer', width: '100%', textAlign: 'center',
                      }}
                    >
                      🔢 Set up a PIN (optional)
                    </button>
                  )}

                  {signupPin.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <div className="ss-auth-input-wrap">
                      <span className="ss-auth-input-icon">🔢</span>
                      <input
                        id="ss-signup-pin"
                        type="password"
                        inputMode="numeric"
                        maxLength={6}
                        className="ss-auth-input"
                        placeholder="6-digit PIN"
                        value={signupPin.trim()}
                        autoFocus
                        onChange={e => {
                          const v = e.target.value.replace(/\D/g, '').slice(0, 6);
                          setSignupPin(v || '\u200b');
                          setSignupPinError('');
                        }}
                        autoComplete="new-password"
                      />
                    </div>

                    {signupPin.length === 6 && (
                      <div className="ss-auth-input-wrap">
                        <span className="ss-auth-input-icon">✓</span>
                        <input
                          type="password"
                          inputMode="numeric"
                          maxLength={6}
                          className="ss-auth-input"
                          placeholder="Confirm PIN"
                          value={signupPinConfirm}
                          onChange={e => {
                            const v = e.target.value.replace(/\D/g, '').slice(0, 6);
                            setSignupPinConfirm(v);
                            if (v.length === 6 && v !== signupPin) {
                              setSignupPinError('PINs do not match.');
                            } else {
                              setSignupPinError('');
                            }
                          }}
                          autoComplete="new-password"
                        />
                      </div>
                    )}

                    {signupPinError && (
                      <p style={{ fontSize: 12, color: '#f87171', margin: 0 }}>{signupPinError}</p>
                    )}

                    {signupPin.length === 6 && signupPinConfirm === signupPin && (
                      <p style={{ fontSize: 12, color: '#22c55e', margin: 0 }}>✓ PIN confirmed</p>
                    )}

                    <button
                      type="button"
                      onClick={() => { setSignupPin(''); setSignupPinConfirm(''); setSignupPinError(''); }}
                      style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.3)', fontSize: 12, cursor: 'pointer', textAlign: 'left', padding: 0 }}
                    >
                      Skip PIN setup
                    </button>
                  </div>
                  )}
                </div>
              )}

              {/* DATE OF BIRTH — signup only */}
              {mode === 'signup' && (
                <div className="ss-auth-field">
                  <label className="ss-auth-label">DATE OF BIRTH</label>
                  <div className="ss-auth-dob-row">
                    <div className="ss-auth-select-wrap">
                      <span className="ss-auth-input-icon"><IconCalendar/></span>
                      <select
                        className="ss-auth-select"
                        value={dobDay}
                        onChange={e => handleDobChange('day', +e.target.value)}
                        required
                      >
                        <option value="">Day</option>
                        {days.map(d => <option key={d} value={d}>{d}</option>)}
                      </select>
                    </div>
                    <div className="ss-auth-select-wrap">
                      <select
                        className="ss-auth-select"
                        value={dobMonth}
                        onChange={e => handleDobChange('month', +e.target.value)}
                        required
                      >
                        <option value="">Month</option>
                        {months.map((m, i) => (
                          <option key={i+1} value={i+1}>{m}</option>
                        ))}
                      </select>
                    </div>
                    <div className="ss-auth-select-wrap">
                      <select
                        className="ss-auth-select"
                        value={dobYear}
                        onChange={e => handleDobChange('year', +e.target.value)}
                        required
                      >
                        <option value="">Year</option>
                        {years.map(y => <option key={y} value={y}>{y}</option>)}
                      </select>
                    </div>
                  </div>
                </div>
              )}

              {/* 18+ note — signup only */}
              {mode === 'signup' && (
                <p className="ss-auth-age-note">
                  You must be 18 or older to play. If you need support, visit{' '}
                  <a
                    href="https://www.gamblinghelponline.org.au"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ss-auth-link"
                  >
                    Gambling Help Online
                  </a>.
                </p>
              )}

              {error && <p className="ss-auth-error">{error}</p>}

              <button
                type="submit"
                className="ss-auth-submit-btn"
                disabled={loading || pinLoading}
              >
                {(loading || pinLoading) ? 'Please wait…' : CTA_LABELS[mode]}
              </button>

              {mode !== 'reset' && (
                <p className="ss-auth-trust">
                  <IconShield/> 100% SECURE. YOUR DATA IS PROTECTED.
                </p>
              )}
            </form>
          )}
        </div>

        {/* Mode switcher under the card */}
        {!resetSent && (
          <p className="ss-auth-switcher">
            {mode === 'signin' && (
              <>Don't have an account?{' '}
                <button className="ss-auth-text-link" onClick={() => switchMode('signup')} type="button">
                  SIGN UP
                </button>
              </>
            )}
            {mode === 'signup' && (
              <>Already have an account?{' '}
                <button className="ss-auth-text-link" onClick={() => switchMode('signin')} type="button">
                  SIGN IN
                </button>
              </>
            )}
            {mode === 'reset' && (
              <button className="ss-auth-text-link" onClick={() => switchMode('signin')} type="button">
                ← BACK TO SIGN IN
              </button>
            )}
          </p>
        )}
      </main>

    </div>
  );
};

export default AuthPage;
